import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { Hero } from '../components/Hero';
import { Outcomes } from '../components/Outcomes';
import { Reviews } from '../components/Reviews';
import { Leak } from '../components/Leak';
import { System } from '../components/System';
import { CTAButton } from '../components/CTAButton';
import { Bloom } from '../components/Bloom';
import { SectionLabel } from '../components/SectionLabel';
import { ThreeServicesCards } from '../components/ThreeServicesCards';

/* Closing section doubles as the homepage's navigation menu. The header nav
   is behind a hamburger on mobile, so someone who scrolls the whole page had
   no in-page route to pricing or why. Three link cards (not three equal
   buttons) so the primary Book a call CTA still wins the hierarchy, and each
   card answers the question a visitor actually has instead of repeating the
   nav label.
   2026-09-17 (local search build): three more cards for the exact-match
   service pages, so the homepage links to them in body copy and not only
   from the nav and footer. */
const routes = [{
  to: '/how-it-works',
  label: 'How it works',
  body: 'Every piece that gets set up, and what each one does for you.'
}, {
  to: '/pricing',
  label: 'Pricing',
  body: 'What it costs, right on the page. You do not have to book a call to find out.'
}, {
  to: '/why',
  label: 'Why Viridia',
  body: 'What makes this different from the last agency that pitched you.'
}, {
  to: '/answering-service-for-contractors',
  label: 'Answering service for contractors',
  body: 'The AI receptionist that picks up on the roof and books the estimate.'
}, {
  to: '/missed-call-text-back',
  label: 'Missed-call text-back',
  body: 'The text that goes out in seconds when a call slips through.'
}, {
  to: '/crm-for-contractors',
  label: 'CRM for contractors',
  body: 'One inbox, a job pipeline, and follow-ups that run themselves.'
}];
export function Home() {
  return <>
      <SEOHead title="AI Receptionist + CRM for Phoenix Home-Service Businesses | Viridia Analytics" description="One system that catches every call, text, and Yelp lead for Phoenix contractors. AI receptionist, CRM, and follow-up that runs on its own. Month to month." canonical="/" jsonLd={LOCAL_BUSINESS_SCHEMA} data-id="element-2694" data-mp="1amt0iwebspnz" />
      <Hero data-id="element-2695" data-mp="289i7zlrsruh8" />

      {/* 2026-09-21 (Kevin, conversion reorder): funnel now runs
          Hero -> agitate the problem (Leak) -> the fix (System) ->
          what changes (Outcomes) -> proof (Reviews) -> the "not ready
          for the whole system" alternate-doors section -> closing nav/CTA.
          Previously the alternate-doors section and Reviews sat directly
          under the hero, before the pitch had made its case -- an exit
          ramp and social proof for an offer the visitor hadn't seen yet.
          They now sit after the System/Outcomes case is made, right before
          the final close, where they belong. */}
      <Leak data-id="element-2696" data-mp="odvz6sjj34nv" />
      <System data-id="element-2697" data-mp="oedt3iipx6sm" />
      <Outcomes data-id="element-2698" data-mp="od2q3kx3k32k" />
      <Reviews data-id="element-2699" data-mp="odmb5ai1n7kt" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-2700" data-mp="og8dmvq2uww6">
        <Bloom size="md" className="left-1/2 top-[-6rem] -translate-x-1/2" data-id="element-2701" data-mp="ken5fnohn6sb" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2702" data-mp="3ssvrecwi8zbi">
          <SectionLabel data-id="element-2703" data-mp="3nu0xaet37oc3">Three ways we work with you</SectionLabel>
          <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2704" data-mp="5nmp6whi28lm">
            Not ready for the whole system? There are two other doors.
          </h2>
          <div className="mt-12" data-id="element-2705" data-mp="1h2t47tyqb4yo">
            <ThreeServicesCards showVisual data-id="element-2706" data-mp="1h1rtxrpjemi0" />
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2707" data-mp="1c4hvxno312ik">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-2708" data-mp="27eepm9kemzhe" />
        <div className="mx-auto w-full max-w-5xl px-6 py-24 lg:py-28" data-id="element-2709" data-mp="27g98lic48hiu">
          <div className="text-center" data-id="element-2710" data-mp="2cd85g8n5z2kq">
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2711" data-mp="2hccb4pavqi6b">
              That&rsquo;s the short version.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-2712" data-mp="2mcgs0gfp9ex2">
              Pick whichever one you came here for.
            </p>
          </div>

          <nav aria-label="Where to next" className="mt-12" data-id="element-2713" data-mp="2vaj1lkdi1fvy">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-2714" data-mp="302u4mw3jdhsq">
              {routes.map((r, i) => <motion.li key={r.to} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} data-id="element-2715" data-mp="3526nl5rae4q1" data-mpi={i}>
                  <Link to={r.to} className="group flex h-full flex-col rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] p-6 transition-colors hover:border-emerald-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]" data-id="element-2716" data-mp="12f8d46buecdg">
                    <span className="flex items-center gap-2 text-base font-semibold text-white" data-id="element-2717" data-mp="1m2zc5sm0sk99">
                      {r.label}
                      <ArrowRightIcon className="h-4 w-4 text-emerald-500 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2718" data-mp="1h3md89hvjmqb" />
                    </span>
                    <span className="mt-2.5 text-sm leading-relaxed text-neutral-400" data-id="element-2719" data-mp="17dt4dg949lpr">
                      {r.body}
                    </span>
                  </Link>
                </motion.li>)}
            </ul>
          </nav>

          <p className="mt-8 text-center text-sm text-neutral-500" data-id="element-2720" data-mp="3uaub5v8dgrg0">
            Based in north Phoenix, serving the whole Valley.{' '}
            <Link to="/answering-service-phoenix" className="text-emerald-500 hover:text-emerald-400" data-id="element-2721" data-mp="25i74ox14fwi">
              Answering service in Phoenix
            </Link>
            {' '}and{' '}
            <Link to="/google-business-profile-setup" className="text-emerald-500 hover:text-emerald-400" data-id="element-2722" data-mp="3piub82saxyjf">
              Google Business Profile setup
            </Link>
            .
          </p>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4" data-id="element-2723" data-mp="3fmr4skqf3dqo">
            <CTAButton to="/contact" data-id="element-2724" data-mp="31motxigj1kkx">Book a 15-min call</CTAButton>
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600" data-id="element-2725" data-mp="36l02zqyzzr23">
              Month to month &middot; No contracts
            </span>
          </div>
        </div>
      </section>
    </>;
}